<template>
  <div
    ref="rootRef"
    class="annotation-editor"
    :class="{ 'annotation-editor--panel': isPanel }"
    @pointerenter="isPointerInside = true"
    @pointerleave="isPointerInside = false"
  >
    <div
      ref="headerRef"
      class="annotation-editor__header"
      :class="{
        'annotation-editor__header--stepping': isPanel && images.length > 1,
        'annotation-editor__header--styling': isPanel && !POINT_TOOLS.includes(activeTool),
      }"
    >
      <icon-button v-if="!readonly && !isPanel" :icon="arrowLeftIcon" size="small" @click="emit('close')" />
      <!-- Docked beside the table the picture's name is edited in place; it saves with the list -->
      <span v-if="isPanel && !readonly" class="annotation-editor__image-name annotation-editor__image-name--editable">
        <fs-bordered-text-input v-model="image.name" placeholder="Picture name" size="large" reset-on-empty />
      </span>
      <span v-else class="annotation-editor__image-name">{{ image.name }}</span>
      <info-tooltip v-if="!readonly && !isPanel">
        <p>
          Select a measurement in the sidebar, then click the image to place a point or click and drag to draw a line.
        </p>
        <p>Drag existing points to reposition them. Use the trash icon in the sidebar to remove annotations.</p>
      </info-tooltip>
      <slot name="header-actions"></slot>
      <annotation-toolbar
        v-if="!readonly && isPanel"
        v-model:active-tool="activeTool"
        v-model:active-style="activeStyle"
        variant="pill"
        with-free-comment
        :disabled-tools="markerNeedsMeasurement ? MARKER_NEEDS_MEASUREMENT : undefined"
      />
      <fs-tooltip
        v-if="!readonly"
        is-delayed
        text="Download picture"
        show-even-if-not-ellipsed
        class="annotation-editor__download"
      >
        <icon-button
          :icon="downloadIcon"
          size="small"
          aria-label="Download picture"
          @click="downloadFile(image.path, image.name)"
        />
      </fs-tooltip>
      <delete-button-with-confirm
        v-if="!readonly && !isPanel"
        :item-name="image.name"
        item-type-name="image"
        :delete-callback="async () => emit('delete')"
        size="small"
      />

      <!-- Docked, the dustbin removes the selected marker, and the picture when none is selected.
           Narrow, the actions that don't fit move from the right into the menu after it: the
           container queries in the styles decide which, and the menu renders inside the header
           so those queries reach its items too. -->
      <template v-if="!readonly && isPanel">
        <icon-button
          class="annotation-editor__delete"
          :icon="trashCanIcon"
          size="small"
          color="red"
          :aria-label="removeLabel"
          @click="removeMarkerOrPicture"
        />
        <icon-button
          class="annotation-editor__more"
          :icon="kebabDotsIcon"
          size="small"
          aria-label="More actions"
          @click="morePopper?.toggle($event)"
        />
        <fs-popper v-if="headerRef" ref="morePopper" :selector="headerRef" placement="bottom-end" :offset="[0, 4]">
          <fs-menu>
            <fs-menu-item
              v-for="tool in COLLAPSIBLE_TOOLS"
              :key="tool"
              :class="`annotation-editor__more-item annotation-editor__more-item--${tool}`"
              :label="ANNOTATION_TOOL_LABELS[tool]"
              :icon="ANNOTATION_TOOL_ICONS[tool]"
              :checked="activeTool === tool"
              @click="activeTool = tool"
            />
            <fs-menu-item
              class="annotation-editor__more-item annotation-editor__more-item--download"
              label="Download picture"
              :icon="downloadIcon"
              @click="downloadFile(image.path, image.name)"
            />
            <fs-menu-item
              class="annotation-editor__more-item annotation-editor__more-item--delete"
              :label="removeLabel"
              :icon="trashCanIcon"
              @click="removeMarkerOrPicture"
            />
          </fs-menu>
        </fs-popper>
        <confirm-deletion-modal
          ref="deletePictureModal"
          :item-name="image.name"
          item-type-name="image"
          :delete-callback="async () => emit('delete')"
        />
      </template>
    </div>

    <annotation-toolbar
      v-if="!readonly && !isPanel"
      v-model:active-tool="activeTool"
      v-model:active-style="activeStyle"
    />

    <div class="annotation-editor__body">
      <div class="annotation-editor__image-area">
        <div
          ref="imageContainerRef"
          class="annotation-editor__image-container"
          :class="{
            'annotation-editor__image-container--grab': isSpaceDown && zoom > 1,
            'annotation-editor__image-container--grabbing': isPanning,
          }"
          @wheel="onWheel"
          @pointerdown="onPanPointerDown"
        >
          <div class="annotation-editor__image-wrapper" :style="wrapperStyle">
            <img ref="imgRef" :src="mainImageSrc" class="annotation-editor__image" @load="onImageLoad" />
            <annotation-canvas
              v-if="canvasSize"
              :annotations="image.annotations"
              :selected-index="readonly ? null : selectedAnnotationIndex"
              :highlighted-linked-entity-id="hoveredMeasurementId ?? highlightedMeasurementId"
              :highlighted-annotation-index="highlightedAnnotationIndex"
              :highlight-shapes="isPanel"
              :no-placement="activeTool === 'marker' && markerNeedsMeasurement"
              :removable="isPanel"
              :width="canvasSize.width"
              :height="canvasSize.height"
              :readonly="readonly"
              :zoom="zoom"
              :active-tool="activeTool"
              :active-style="activeStyle"
              :label-fn="makeAnnotationLabelFn(image.annotations)"
              :style="{ pointerEvents: isSpaceDown && zoom > 1 ? 'none' : undefined }"
              @add-point="handleAddPoint"
              @add-line="handleAddLine"
              @add="handleAdd"
              @select="handleSelect"
              @move-point="handleMovePoint"
              @move-endpoint="handleMoveEndpoint"
              @convert-to-curve="handleConvertToCurve"
              @convert-to-line="handleConvertToLine"
              @hover="onAnnotationHover"
              @annotation-click="onAnnotationClick"
              @remove="handleDelete"
            />
          </div>
        </div>
        <div class="annotation-editor__zoom-control">
          <span class="annotation-editor__zoom-label">{{ Math.round(zoom * 100) }}%</span>
          <fs-slider
            v-model="zoom"
            :min="1"
            :max="5"
            :interval="0.01"
            tooltip="none"
            class="annotation-editor__zoom-slider"
            @update:model-value="clampPan()"
          />
        </div>
        <div v-if="images.length > 1 && !isPanel" class="annotation-editor__thumbnails">
          <div
            v-for="(img, idx) in images"
            :key="img.path"
            class="annotation-editor__thumbnail"
            :class="{ 'annotation-editor__thumbnail--active': img === image }"
            @click="emit('switch-image', idx)"
          >
            <img :src="imagePath(img.path, 120, 120, 'fit')" :alt="img.name" />
            <annotation-canvas
              v-if="img.annotations?.length && img.width && img.height"
              :annotations="img.annotations"
              :width="img.width"
              :height="img.height"
              :scale="0.35"
              :label-fn="makeAnnotationLabelFn(img.annotations)"
              readonly
              class="annotation-editor__thumbnail-overlay"
            />
          </div>
        </div>
      </div>

      <div v-if="!isPanel && (measurements.length || image.annotations.length)" class="annotation-editor__sidebar">
        <template v-if="measurements.length">
          <div class="annotation-editor__sidebar-title">Measurements</div>
          <div class="annotation-editor__measurements-list">
            <div
              v-for="measurement in measurements"
              :key="measurement.measurementId"
              class="annotation-editor__measurement-row"
              :class="{
                'annotation-editor__measurement-row--selected':
                  !readonly && pendingMeasurementId === measurement.measurementId,
                'annotation-editor__measurement-row--highlighted': hoveredMeasurementId === measurement.measurementId,
              }"
              @click="!readonly && toggleMeasurement(measurement.measurementId)"
              @mouseenter="hoveredMeasurementId = measurement.measurementId"
              @mouseleave="hoveredMeasurementId = null"
            >
              <annotation-circle
                :linked="measurementHasAnnotation(measurement.measurementId)"
                :hovered="hoveredMeasurementId === measurement.measurementId"
              >
                {{ measurementsStore.byId[measurement.measurementId]?.annotationName || '' }}
              </annotation-circle>
              <span class="annotation-editor__measurement-name">
                {{ measurementsStore.byId[measurement.measurementId]?.name || '' }}
              </span>
            </div>
          </div>
        </template>

        <!-- Annotation comments (readonly) -->
        <template v-if="readonly">
          <template v-if="readonlyComments.length">
            <div class="annotation-editor__sidebar-title">Comments</div>
            <div class="annotation-editor__comments-list">
              <div
                v-for="c in readonlyComments"
                :key="`readonly-${c.originalIndex}`"
                class="annotation-editor__comment-row"
                :class="{ 'annotation-editor__comment-row--highlighted': hoveredAnnotationIndex === c.originalIndex }"
                @mouseenter="
                  hoveredAnnotationIndex = c.originalIndex;
                  hoveredMeasurementId = c.measurementId;
                "
                @mouseleave="
                  hoveredAnnotationIndex = null;
                  hoveredMeasurementId = null;
                "
              >
                <annotation-circle linked :hovered="hoveredAnnotationIndex === c.originalIndex">
                  {{ c.label }}
                </annotation-circle>
                <span class="annotation-editor__comment-text">{{ c.comment }}</span>
              </div>
            </div>
          </template>
        </template>
        <!-- Annotation comments (edit) -->
        <template v-else>
          <div v-if="linkedAnnotations.length || unlinkedAnnotations.length" class="annotation-editor__sidebar-title">
            Comments
          </div>
          <div v-if="linkedAnnotations.length || unlinkedAnnotations.length" class="annotation-editor__comments-list">
            <div
              v-for="annotation in linkedAnnotations"
              :key="`comment-${annotation.originalIndex}`"
              class="annotation-editor__comment-row"
              :class="{
                'annotation-editor__comment-row--selected': selectedAnnotationIndex === annotation.originalIndex,
                'annotation-editor__comment-row--highlighted': hoveredAnnotationIndex === annotation.originalIndex,
              }"
              @click="handleSelect(annotation.originalIndex)"
              @mouseenter="
                hoveredAnnotationIndex = annotation.originalIndex;
                hoveredMeasurementId = annotation.annotation.linkedEntityId ?? null;
              "
              @mouseleave="
                hoveredAnnotationIndex = null;
                hoveredMeasurementId = null;
              "
            >
              <annotation-circle linked :hovered="hoveredAnnotationIndex === annotation.originalIndex">
                {{ getAnnotationLabel(annotation.annotation) }}
              </annotation-circle>
              <fs-input
                :ref="(el: any) => setCommentRef(annotation.originalIndex, el)"
                :model-value="annotation.annotation.comment ?? ''"
                class="annotation-editor__comment-input"
                variant="min-code"
                placeholder="Add comment..."
                @update:model-value="annotation.annotation.comment = $event ? String($event) : undefined"
                @focus="selectedAnnotationIndex = annotation.originalIndex"
              />
              <icon-button
                :icon="trashCanIcon"
                size="tiny"
                color="red"
                @click.stop="handleDelete(annotation.originalIndex)"
              />
            </div>
            <!-- Unlinked annotations -->
            <div
              v-for="(annotation, uIdx) in unlinkedAnnotations"
              :key="`unlinked-${annotation.originalIndex}`"
              class="annotation-editor__comment-row"
              :class="{
                'annotation-editor__comment-row--selected': selectedAnnotationIndex === annotation.originalIndex,
                'annotation-editor__comment-row--highlighted': hoveredAnnotationIndex === annotation.originalIndex,
              }"
              @click="handleSelect(annotation.originalIndex)"
              @mouseenter="hoveredAnnotationIndex = annotation.originalIndex"
              @mouseleave="hoveredAnnotationIndex = null"
            >
              <annotation-circle linked :hovered="hoveredAnnotationIndex === annotation.originalIndex">
                #{{ uIdx + 1 }}
              </annotation-circle>
              <fs-input
                :ref="(el: any) => setCommentRef(annotation.originalIndex, el)"
                :model-value="annotation.annotation.comment ?? ''"
                class="annotation-editor__comment-input"
                variant="min-code"
                placeholder="Add comment..."
                @update:model-value="annotation.annotation.comment = $event ? String($event) : undefined"
                @focus="selectedAnnotationIndex = annotation.originalIndex"
              />
              <icon-button
                :icon="trashCanIcon"
                size="tiny"
                color="red"
                @click.stop="handleDelete(annotation.originalIndex)"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount, useTemplateRef, type CSSProperties } from 'vue';
import type {
  AnnotationGeometry,
  AnnotationItem,
  AnnotationStyle,
  LineGeometry,
  CurveGeometry,
  MChartImageDto,
  MChartMeasurementDto,
} from '@/ApiClient';
import { useMeasurements } from '@/store/measurements';
import { blobUrl } from '@lib/api';
import { imagePath } from '@lib/imgproxy';
import AnnotationCanvas from '@components/annotations/annotation-canvas.vue';
import AnnotationToolbar from '@components/annotations/annotation-toolbar.vue';
import {
  ANNOTATION_TOOL_ICONS,
  ANNOTATION_TOOL_LABELS,
  POINT_TOOLS,
  type AnnotationTool,
} from '@components/annotations/annotation-toolbar.vue';
import { makeAnnotationLabelFn } from './mchart-utils';
import IconButton from '@components/icon-button.vue';
import AnnotationCircle from '@components/annotation-circle.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import FsSlider from '@components/form-elements/fs-slider.vue';
import InfoTooltip from '@components/info-tooltip.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import FsBorderedTextInput from '@components/form-elements/fs-bordered-text-input.vue';
import DeleteButtonWithConfirm from '@components/delete-button-with-confirm.vue';
import ConfirmDeletionModal from '@components/modals/confirm-deletion-modal.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import FsMenu from '@components/fs-menu/index.vue';
import FsMenuItem from '@components/fs-menu/fs-menu-item.vue';
import { downloadFile } from '@lib/files';
import arrowLeftIcon from '@icons/other/arrow-left.svg';
import downloadIcon from '@icons/interface/download.svg';
import trashCanIcon from '@icons/other/trash-can-icon.svg';
import kebabDotsIcon from '@icons/other/kebab-dots-icon.svg';

const {
  measurements,
  readonly = false,
  images = [],
  fullResolution = false,
  layout,
  highlightedMeasurementId = null,
  selectedMeasurementId = null,
} = defineProps<{
  measurements: Pick<MChartMeasurementDto, 'measurementId'>[];
  readonly?: boolean;
  images?: MChartImageDto[];
  /**
   * Serve the original rather than a resized copy. Off by default: a measurement chart's photos
   * are large and there are many of them, and the annotations are drawn against the displayed
   * size either way. On where the zoom is the point — magnifying a 1200px copy only magnifies
   * the resampling.
   */
  fullResolution?: boolean;
  // Docked beside the measurements table, which takes the place of the measurement list.
  // A click on the image only places a marker once a measurement is picked.
  layout?: 'panel';
  highlightedMeasurementId?: number | null;
  // The point selected in the table; its marker on this picture is selected here too.
  selectedMeasurementId?: number | null;
}>();

const image = defineModel<MChartImageDto>({ required: true });

const isPanel = computed(() => layout === 'panel');

// The measurement the next marker or line is linked to: picked in the sidebar here, or, docked in
// the panel, whichever is selected in the table
const pickedMeasurementId = ref<number | null>(null);
const pendingMeasurementId = computed(() =>
  isPanel.value ? (readonly ? null : selectedMeasurementId) : pickedMeasurementId.value
);

// Letting go of it: in the panel that is the table's selection
function clearPendingMeasurement() {
  if (!isPanel.value) pickedMeasurementId.value = null;
  else if (pendingMeasurementId.value != null) emit('select-measurement', null);
}

// The header's overflow menu, and the tools that can move into it: the drawing ones
const headerRef = useTemplateRef('headerRef');
// fs-popper doesn't type its methods
const morePopper = ref<any>(null);
const COLLAPSIBLE_TOOLS = (Object.keys(ANNOTATION_TOOL_ICONS) as AnnotationTool[]).filter(
  (tool) => !POINT_TOOLS.includes(tool)
);

const deletePictureModal = useTemplateRef('deletePictureModal');
const removeLabel = computed(() => (selectedAnnotationIndex.value !== null ? 'Remove marker' : 'Delete picture'));

function removeMarkerOrPicture() {
  if (selectedAnnotationIndex.value !== null) handleDelete(selectedAnnotationIndex.value);
  else deletePictureModal.value?.open();
}

// Docked, a marker is always placed for a measurement; free ones have their own tool
const MARKER_NEEDS_MEASUREMENT = { marker: 'Select a measurement to place a marker' };
const markerNeedsMeasurement = computed(() => isPanel.value && pendingMeasurementId.value == null);

const mainImageSrc = computed(() =>
  fullResolution ? `${blobUrl}/${image.value.path}` : imagePath(image.value.path, 1200, 900, 'fit')
);

const emit = defineEmits<{
  close: [];
  delete: [];
  'switch-image': [index: number];
  'select-measurement': [measurementId: number | null];
  'hover-measurement': [measurementId: number | null];
}>();

const measurementsStore = useMeasurements();

const selectedAnnotationIndex = defineModel<number | null>('selectedAnnotationIndex', { default: null });
const hoveredMeasurementId = ref<number | null>(null);
const hoveredAnnotationIndex = ref<number | null>(null);

const activeTool = ref<AnnotationTool>('marker');
const activeStyle = ref<AnnotationStyle>({ color: '#35495E', strokeWidth: 2, opacity: 1 });

// Docked, picking a measurement arms the marker tool, so the next click places it whichever tool
// was in use
watch(pendingMeasurementId, (id) => {
  if (isPanel.value && id != null) activeTool.value = 'marker';
});

// Undo/redo stacks
const undoStack = ref<string[]>([]);
const redoStack = ref<string[]>([]);
const MAX_UNDO = 50;

function pushUndo() {
  undoStack.value.push(JSON.stringify(image.value.annotations));
  if (undoStack.value.length > MAX_UNDO) undoStack.value.shift();
  redoStack.value = [];
}

function undo() {
  const prev = undoStack.value.pop();
  if (prev) {
    redoStack.value.push(JSON.stringify(image.value.annotations));
    image.value.annotations = JSON.parse(prev);
    selectedAnnotationIndex.value = null;
  }
}

function redo() {
  const next = redoStack.value.pop();
  if (next) {
    undoStack.value.push(JSON.stringify(image.value.annotations));
    image.value.annotations = JSON.parse(next);
    selectedAnnotationIndex.value = null;
  }
}

const imageContainerRef = ref<HTMLElement | null>(null);
const imgRef = ref<HTMLImageElement | null>(null);
const canvasSize = ref<{ width: number; height: number } | null>(null);

// Zoom/pan state
const zoom = ref(1);
const panX = ref(0);
const panY = ref(0);
const isSpaceDown = ref(false);
const isPanning = ref(false);

const containerSize = ref<{ width: number; height: number }>({ width: 0, height: 0 });

let containerObserver: ResizeObserver | null = null;

onMounted(() => {
  if (imageContainerRef.value) {
    containerObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        containerSize.value = { width: entry.contentRect.width, height: entry.contentRect.height };
      }
    });
    containerObserver.observe(imageContainerRef.value);
  }
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
});

onBeforeUnmount(() => {
  containerObserver?.disconnect();
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('keyup', onKeyUp);
});

const renderedImageSize = computed(() => {
  if (!canvasSize.value || !containerSize.value.width || !containerSize.value.height) return null;
  const iw = canvasSize.value.width;
  const ih = canvasSize.value.height;
  const cw = containerSize.value.width;
  const ch = containerSize.value.height;
  const scale = Math.min(cw / iw, ch / ih);
  return { width: iw * scale, height: ih * scale };
});

const wrapperStyle = computed<CSSProperties>(() => {
  const size = renderedImageSize.value;
  if (!size) return {};
  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    flexShrink: 0,
    transform: zoom.value > 1 ? `scale(${zoom.value}) translate(${panX.value}px, ${panY.value}px)` : undefined,
    transformOrigin: 'center center',
  };
});

function clampPan() {
  if (zoom.value <= 1) {
    panX.value = 0;
    panY.value = 0;
    return;
  }
  const size = renderedImageSize.value;
  if (!size) return;
  const maxPanX = (size.width / 2) * (1 - 1 / zoom.value);
  const maxPanY = (size.height / 2) * (1 - 1 / zoom.value);
  panX.value = Math.max(-maxPanX, Math.min(maxPanX, panX.value));
  panY.value = Math.max(-maxPanY, Math.min(maxPanY, panY.value));
}

// Wheel zoom with rAF batching
let wheelRafId: number | null = null;
let accumulatedWheelDelta = 0;
let lastWheelClientX = 0;
let lastWheelClientY = 0;

function onWheel(event: WheelEvent) {
  const isZoomGesture = event.ctrlKey || event.metaKey;

  if (!isZoomGesture && zoom.value <= 1) return;

  event.preventDefault();

  if (isZoomGesture) {
    accumulatedWheelDelta += event.deltaY;
    lastWheelClientX = event.clientX;
    lastWheelClientY = event.clientY;

    if (wheelRafId !== null) return;

    wheelRafId = requestAnimationFrame(() => {
      const container = imageContainerRef.value;
      const size = renderedImageSize.value;
      if (!container || !size) {
        wheelRafId = null;
        accumulatedWheelDelta = 0;
        return;
      }

      const rect = container.getBoundingClientRect();
      // Cursor position relative to wrapper center, in pre-zoom coords
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const cursorOffsetX = (lastWheelClientX - centerX) / zoom.value - panX.value;
      const cursorOffsetY = (lastWheelClientY - centerY) / zoom.value - panY.value;

      const oldZoom = zoom.value;
      const delta = accumulatedWheelDelta > 0 ? -0.1 : 0.1;
      const steps = Math.min(Math.abs(accumulatedWheelDelta) / 10, 5);
      zoom.value = Math.max(1, Math.min(5, oldZoom + delta * steps));

      // Zoom-to-cursor: adjust pan so point under cursor stays fixed
      if (zoom.value !== oldZoom) {
        panX.value = (lastWheelClientX - centerX) / zoom.value - cursorOffsetX;
        panY.value = (lastWheelClientY - centerY) / zoom.value - cursorOffsetY;
      }

      if (zoom.value <= 1) {
        panX.value = 0;
        panY.value = 0;
      } else {
        clampPan();
      }

      wheelRafId = null;
      accumulatedWheelDelta = 0;
    });
  } else {
    // Trackpad pan when zoomed in
    panX.value -= event.deltaX / zoom.value;
    panY.value -= event.deltaY / zoom.value;
    clampPan();
  }
}

// Docked beside the table, shortcuts only apply while working in the editor, so that
// Backspace or undo elsewhere on the page don't reach its annotations.
const rootRef = ref<HTMLElement | null>(null);
const isPointerInside = ref(false);

// Space+drag pan + undo + delete + escape
function onKeyDown(e: KeyboardEvent) {
  if (isPanel.value && !isPointerInside.value && !rootRef.value?.contains(document.activeElement)) {
    // A point is armed from the table, so Esc has to reach it from there too.
    if (e.key === 'Escape') clearPendingMeasurement();
    return;
  }
  if (e.code === 'Space' && zoom.value > 1 && !e.repeat) {
    e.preventDefault();
    isSpaceDown.value = true;
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !readonly) {
    e.preventDefault();
    undo();
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || (e.key === 'z' && e.shiftKey)) && !readonly) {
    e.preventDefault();
    redo();
  }
  if ((e.key === 'Delete' || e.key === 'Backspace') && selectedAnnotationIndex.value !== null && !readonly) {
    // Don't delete if focus is in an input
    if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
    e.preventDefault();
    handleDelete(selectedAnnotationIndex.value);
  }
  if (e.key === 'Escape') {
    selectedAnnotationIndex.value = null;
    clearPendingMeasurement();
  }
}

function onKeyUp(e: KeyboardEvent) {
  if (e.code === 'Space') {
    isSpaceDown.value = false;
  }
}

let panStartPos: { x: number; y: number } | null = null;
let panStartPan: { x: number; y: number } | null = null;

function onPanPointerDown(event: PointerEvent) {
  if (!isSpaceDown.value || zoom.value <= 1) return;
  event.preventDefault();
  isPanning.value = true;
  panStartPos = { x: event.clientX, y: event.clientY };
  panStartPan = { x: panX.value, y: panY.value };

  const onPointerMove = (e: PointerEvent) => {
    if (!panStartPos || !panStartPan) return;
    panX.value = panStartPan.x + (e.clientX - panStartPos.x) / zoom.value;
    panY.value = panStartPan.y + (e.clientY - panStartPos.y) / zoom.value;
    clampPan();
  };

  const onPointerUp = () => {
    isPanning.value = false;
    panStartPos = null;
    panStartPan = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
}

// Comment input refs for focus
const commentRefs = ref<Record<number, any>>({});

function setCommentRef(index: number, el: any) {
  if (el) {
    commentRefs.value[index] = el;
  } else {
    delete commentRefs.value[index];
  }
}

// Separate linked vs unlinked annotations
const linkedAnnotations = computed(() =>
  image.value.annotations
    .map((annotation, originalIndex) => ({ annotation, originalIndex }))
    .filter((a) => a.annotation.linkedEntityId)
);

const unlinkedAnnotations = computed(() =>
  image.value.annotations
    .map((annotation, originalIndex) => ({ annotation, originalIndex }))
    .filter((a) => !a.annotation.linkedEntityId)
);

const readonlyComments = computed(() => {
  const comments: { label: string; comment: string; measurementId: number | null; originalIndex: number }[] = [];
  let unlinkedCount = 0;
  for (let i = 0; i < image.value.annotations.length; i++) {
    const a = image.value.annotations[i];
    if (!a.linkedEntityId) unlinkedCount++;
    if (!a.comment) continue;
    const label = a.linkedEntityId
      ? (measurementsStore.byId[a.linkedEntityId]?.annotationName ?? '')
      : `#${unlinkedCount}`;
    comments.push({ label, comment: a.comment, measurementId: a.linkedEntityId ?? null, originalIndex: i });
  }
  return comments;
});

// By path, not id: a new picture gets its id when the list saves, long
// after it loaded, and a reset then would leave it without a canvas
watch(
  () => image.value.path,
  () => {
    canvasSize.value = null;
    selectedAnnotationIndex.value = null;
    zoom.value = 1;
    panX.value = 0;
    panY.value = 0;
    undoStack.value = [];
    redoStack.value = [];
  }
);

function onImageLoad() {
  const img = imgRef.value;
  if (!img) return;
  canvasSize.value = { width: img.naturalWidth, height: img.naturalHeight };
}

function toggleMeasurement(measurementId: number) {
  pickedMeasurementId.value = pickedMeasurementId.value === measurementId ? null : measurementId;
}

function measurementHasAnnotation(measurementId: number): boolean {
  return image.value.annotations.some((a) => a.linkedEntityId === measurementId);
}

function getAnnotationLabel(annotation: AnnotationItem): string {
  if (!annotation.linkedEntityId) return '';
  return measurementsStore.byId[annotation.linkedEntityId]?.annotationName ?? '';
}

function focusComment(index: number) {
  nextTick(() => {
    const inputComponent = commentRefs.value[index];
    if (inputComponent?.$el) {
      const input = inputComponent.$el.querySelector('input') ?? inputComponent.$el;
      input.focus?.();
    }
  });
}

function handleAddPoint(payload: { x: number; y: number }) {
  // The comment tool places a marker tied to no point, armed or not
  addMarker(
    { type: 'PointGeometry', x: payload.x, y: payload.y },
    activeTool.value === 'comment' ? undefined : (pendingMeasurementId.value ?? undefined)
  );
}

function handleAddLine(payload: { x1: number; y1: number; x2: number; y2: number }) {
  addMarker(
    { type: 'LineGeometry', x1: payload.x1, y1: payload.y1, x2: payload.x2, y2: payload.y2 },
    pendingMeasurementId.value ?? undefined
  );
}

function addMarker(geometry: AnnotationGeometry, linkedEntityId: number | undefined) {
  pushUndo();
  image.value.annotations.push({ id: crypto.randomUUID(), geometry, linkedEntityId });
  const newIndex = image.value.annotations.length - 1;
  selectedAnnotationIndex.value = newIndex;
  focusComment(newIndex);
  // The panel keeps the point armed, so each click adds another marker until Esc.
  if (!isPanel.value) pickedMeasurementId.value = null;
}

function handleAdd(payload: { geometry: AnnotationGeometry; style?: AnnotationStyle }) {
  pushUndo();
  image.value.annotations.push({
    id: crypto.randomUUID(),
    geometry: payload.geometry,
    style: payload.style,
  });
  const newIndex = image.value.annotations.length - 1;
  selectedAnnotationIndex.value = newIndex;
}

// Pressing a marker to drag it selects it here, and re-renders it before any click arrives.
function handleSelect(index: number) {
  selectedAnnotationIndex.value = index;
  focusComment(index);
  const linkedEntityId = image.value.annotations[index]?.linkedEntityId;
  if (linkedEntityId) emit('select-measurement', linkedEntityId);
}

function handleMovePoint(payload: { index: number; x: number; y: number }) {
  pushUndo();
  const annotation = image.value.annotations[payload.index];
  if (!annotation) return;
  annotation.geometry = { type: 'PointGeometry', x: payload.x, y: payload.y };
}

function handleMoveEndpoint(payload: { index: number; endpoint: 'p1' | 'p2' | 'cp'; x: number; y: number }) {
  pushUndo();
  const annotation = image.value.annotations[payload.index];
  if (!annotation) return;
  const geo = annotation.geometry;
  if (payload.endpoint === 'cp' && 'cx' in geo) {
    annotation.geometry = { ...(geo as CurveGeometry), cx: payload.x, cy: payload.y };
  } else if (payload.endpoint === 'p1' && 'x1' in geo) {
    annotation.geometry = { ...(geo as LineGeometry), x1: payload.x, y1: payload.y };
  } else if (payload.endpoint === 'p2' && 'x2' in geo) {
    annotation.geometry = { ...(geo as LineGeometry), x2: payload.x, y2: payload.y };
  }
}

function handleConvertToCurve(payload: { index: number; cx: number; cy: number }) {
  pushUndo();
  const annotation = image.value.annotations[payload.index];
  if (!annotation) return;
  const geo = annotation.geometry as LineGeometry;
  annotation.geometry = {
    type: 'CurveGeometry',
    x1: geo.x1,
    y1: geo.y1,
    x2: geo.x2,
    y2: geo.y2,
    cx: payload.cx,
    cy: payload.cy,
  };
}

function handleConvertToLine(payload: { index: number }) {
  pushUndo();
  const annotation = image.value.annotations[payload.index];
  if (!annotation) return;
  const geo = annotation.geometry as CurveGeometry;
  annotation.geometry = {
    type: 'LineGeometry',
    x1: geo.x1,
    y1: geo.y1,
    x2: geo.x2,
    y2: geo.y2,
  };
}

function onAnnotationHover(payload: { index: number; linkedEntityId: number | null } | null) {
  if (!payload) {
    hoveredMeasurementId.value = null;
    hoveredAnnotationIndex.value = null;
  } else {
    hoveredAnnotationIndex.value = payload.index;
    hoveredMeasurementId.value = payload.linkedEntityId;
  }
  emit('hover-measurement', payload?.linkedEntityId ?? null);
}

// Any tool, readonly too: a click selects the annotation and the point it is linked to.
function onAnnotationClick(payload: { index: number; linkedEntityId: number | null }) {
  selectedAnnotationIndex.value = payload.index;
  if (payload.linkedEntityId) emit('select-measurement', payload.linkedEntityId);
}

const selectedAnnotation = computed(() =>
  selectedAnnotationIndex.value !== null ? (image.value.annotations[selectedAnnotationIndex.value] ?? null) : null
);

// In the panel a point can have several markers; while one of them is selected it alone stands out,
// unless another point is being pointed at. A selected free comment always stands out.
const highlightedAnnotationIndex = computed(() => {
  if (hoveredAnnotationIndex.value !== null) return hoveredAnnotationIndex.value;
  if (!isPanel.value || !selectedAnnotation.value) return null;
  const linkedId = selectedAnnotation.value.linkedEntityId;
  if (!linkedId) return selectedAnnotationIndex.value;
  return (hoveredMeasurementId.value ?? highlightedMeasurementId) === linkedId ? selectedAnnotationIndex.value : null;
});

// Picking a point in the table, or another picture, selects the point's first marker here, or
// nothing when it has none. Removing a marker selects no other in its place.
watch(
  () => [selectedMeasurementId, image.value] as const,
  ([measurementId]) => {
    if (!isPanel.value) return;
    if (selectedAnnotation.value?.linkedEntityId && selectedAnnotation.value.linkedEntityId === measurementId) return;
    const linkedIndex =
      measurementId != null ? image.value.annotations.findIndex((a) => a.linkedEntityId === measurementId) : -1;
    selectedAnnotationIndex.value = linkedIndex !== -1 ? linkedIndex : null;
  }
);

defineExpose({ deleteAnnotation: (index: number) => handleDelete(index) });

function handleDelete(index: number) {
  pushUndo();
  image.value.annotations.splice(index, 1);
  if (selectedAnnotationIndex.value === index) {
    selectedAnnotationIndex.value = null;
  } else if (selectedAnnotationIndex.value !== null && selectedAnnotationIndex.value > index) {
    selectedAnnotationIndex.value--;
  }
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Docked, the header's actions leave it from the right for its "More actions" menu as it narrows,
// so the leftmost stay longest. Each width is the item plus the gap before it; icon buttons are 2.3rem.
// The pill's sizes mirror annotation-toolbar's --pill styles: keep them in step.
$header-gap: 0.4rem;
$header-icon-slot: 2.3rem + $header-gap;
$pill-tool: 2.8rem;
$pill-gap: 0.2rem;
$pill-padding: 0.2rem;
$header-tool-slot: $pill-tool + $pill-gap;
$header-name-min: 8rem;
// Always there: the name, Marker and Comment in the pill, and the dustbin's slot, which the menu
// button takes once the dustbin has gone
$header-kept: $header-name-min + $header-gap + 2 * $pill-padding + 2 * $pill-tool + $pill-gap + $header-icon-slot;
// Shown only at times: the previous/next picture pair, and the swatch while a shape tool is active
$header-stepping: 2 * (2.8rem + $header-gap);
$header-styling: $header-tool-slot;
// After the dustbin, rightmost first
$header-collapsible: (
  download: $header-icon-slot,
  highlighter: $header-tool-slot,
  freehand: $header-tool-slot,
  ellipse: $header-tool-slot,
  rectangle: $header-tool-slot,
  arrow: $header-tool-slot,
);

@function sum-widths($widths) {
  $sum: 0rem;
  @each $id, $width in $widths {
    $sum: $sum + $width;
  }
  @return $sum;
}

@mixin collapse-header-actions($extra) {
  $rest: sum-widths($header-collapsible);

  @container annotation-header (width < #{$header-kept + $extra + $rest}) {
    .annotation-editor__more {
      display: inline-flex;
    }

    .annotation-editor__delete {
      display: none;
    }

    .annotation-editor__more-item--delete {
      display: flex;
    }
  }

  @each $id, $width in $header-collapsible {
    @container annotation-header (width < #{$header-kept + $extra + $rest}) {
      @if $id == download {
        .annotation-editor__download {
          display: none;
        }
      } @else {
        :deep(.annotation-toolbar__tool-wrap--#{$id}) {
          display: none;
        }
      }

      .annotation-editor__more-item--#{$id} {
        display: flex;
      }
    }

    $rest: $rest - $width;
  }
}

.annotation-editor {
  display: flex;
  flex-direction: column;
  height: 100%;

  // Docked: the image on top at a fixed height, the annotation comments underneath.
  &--panel {
    height: auto;

    .annotation-editor__header {
      container: annotation-header / inline-size;
      position: relative;
      z-index: 3;
      gap: 0.4rem;
      padding: 0 0 1rem;
      border-bottom: none;
    }

    .annotation-editor__image-name {
      font-family: 'Montserrat', sans-serif;
      font-size: 1.4rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .annotation-editor__body {
      flex-direction: column;
    }

    .annotation-editor__image-area {
      padding: 1.2rem 0 0;
    }

    .annotation-editor__image-container {
      flex: none;
      width: 100%;
      aspect-ratio: 3 / 4;
      // The host knows how much of the window its own chrome takes
      max-height: var(--annotation-editor-image-max-height, none);
      border-radius: 1.4rem;
      background: $c-surface-muted;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    padding: 1.6rem 2.4rem;
    border-bottom: 1px solid rgba($c-dark, 0.1);

    // The icons' tooltips; the name's inline input is one too, but sizes itself as a grid
    :deep(.fs-tooltip:not(.fs-input-text)) {
      display: inline-flex;
      align-items: center;
    }

    :deep(.info-tooltip) {
      width: 2rem;
      height: 2rem;
    }
  }

  &__image-name {
    flex: 1;
    font-size: 1.4rem;
    font-weight: 500;
    color: $c-dark;

    // Only as wide as the name, so its hover outline hugs the text; the margin keeps the tools
    // right. Sized here rather than on the input, whose inner field inherits all its styles.
    &--editable {
      display: flex;
      flex: 0 1 auto;
      min-width: 0;
      margin-right: auto;

      :deep(.fs-input-text) {
        max-width: 100%;
      }
    }
  }

  &__body {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  &__image-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    padding: 2.4rem;
  }

  &__image-container {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &--grab {
      cursor: grab;
    }

    &--grabbing {
      cursor: grabbing;
    }
  }

  &__image-wrapper {
    position: relative;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__zoom-control {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    padding-top: 1.2rem;
  }

  &__zoom-label {
    font-size: 1.2rem;
    color: rgba($c-dark, 0.5);
    white-space: nowrap;
  }

  &__zoom-slider {
    width: 16rem !important;
  }

  &__thumbnails {
    display: flex;
    justify-content: center;
    gap: 0.8rem;
    padding-top: 1.2rem;
    overflow-x: auto;
  }

  &__thumbnail {
    position: relative;
    flex-shrink: 0;
    width: 6rem;
    height: 6rem;
    border-radius: 0.6rem;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    opacity: 0.6;
    transition: all 0.15s ease;

    &:hover {
      opacity: 0.9;
    }

    &--active {
      border-color: $c-blue;
      opacity: 1;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__thumbnail-overlay {
    pointer-events: none;
  }

  &__sidebar {
    width: 28rem;
    border-left: 1px solid rgba($c-dark, 0.1);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  &__sidebar-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: rgba($c-dark, 0.5);
    text-transform: uppercase;
    padding: 1.6rem 1.6rem 1rem;
  }

  &__measurements-list {
    padding: 0 0.8rem;
  }

  &__comments-list {
    padding: 0 0.8rem;
    padding-bottom: 1.6rem;
  }

  &__measurement-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.6rem 0.8rem;
    border-radius: 0.4rem;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &--highlighted {
      background-color: rgba($c-dark, 0.04);
    }

    &--selected {
      background-color: rgba($c-blue, 0.08);
      outline: 2px solid $c-blue;
      outline-offset: -2px;
    }

    &--selected-annotation {
      background-color: rgba($c-blue, 0.06);
    }
  }

  &__comment-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.4rem 0.8rem;
    border-radius: 0.4rem;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &--highlighted {
      background-color: rgba($c-dark, 0.03);
    }

    &--selected {
      background-color: rgba($c-blue, 0.06);
    }
  }

  &__comment-input {
    flex: 1;
    min-width: 0;
  }

  &__comment-text {
    font-size: 1.2rem;
    color: rgba($c-dark, 0.7);
    flex: 1;
    min-width: 0;
  }

  &__measurement-name {
    font-size: 1.2rem;
    color: $c-dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }

  // Last, so hiding wins over the header's own display rules for its tooltips
  &--panel {
    .annotation-editor__more,
    .annotation-editor__more-item {
      display: none;
    }

    .annotation-editor__header {
      @include collapse-header-actions(0rem);
    }

    .annotation-editor__header--stepping {
      @include collapse-header-actions($header-stepping);
    }

    .annotation-editor__header--styling {
      @include collapse-header-actions($header-styling);
    }

    .annotation-editor__header--stepping.annotation-editor__header--styling {
      @include collapse-header-actions($header-stepping + $header-styling);
    }
  }
}
</style>
